import {
  CustomDomain,
  Named,
  PermissionAccessLevel,
  UserManagementSettings,
} from './apiTypes';
import { Entity, Versioned } from './coreTypes';
import { FeatureKeys } from './featureTypes';

export type APIPublicOrganizationAttributes = Entity &
  Versioned &
  Named & {
    label: string;
    features: Record<FeatureKeys, boolean>;
    settings: APIOrganizationSettings;
  };

/**
 * The backend returns only a subset of the organization attributes
 * depending on the user's role
 */
export type APIAdminOrganizationAttributes = APIPublicOrganizationAttributes & {
  role: string;
  members: number;

  ardoq: {
    'has-internal-user-management':
      | UserManagementSettings.IN_APP_ONLY
      | UserManagementSettings.IN_APP_AND_EXTERNALLY
      | null;
  };

  db: { name: string };
  hasConsentedToViewData?: boolean;
  'is-trial': boolean;
  'days-remaining-trial': number;
  expired: boolean;
  'custom-domain': CustomDomain | null;
};

/**
 * The organization entity has only been available to admin users, but has recently
 * been made available to all users with limited attributes.
 * This type guard can be used to differentiate between the two.
 * Eventually we hope to make the whole entity available to all users.
 */
export const isAdminOrganizationAttributes = (
  organization: APIAdminOrganizationAttributes | APIPublicOrganizationAttributes
): organization is APIAdminOrganizationAttributes => {
  return 'members' in organization;
};

export type APIConstraintEnforcementMode = 'off' | 'warning' | 'strict';

export type APIBannerSettings = {
  bannerImage?: string | null;
  /** @deprecated No longer used, see #28097 */
  showLogoOnBanner?: boolean | null;
  bannerTheme?: string | null;
  customMessageTitle?: string | null;
  customMessage?: string | null;
};

export type APILogoSettings = {
  companyLogo?: string | null;
  publicLogo?: string | null;
};

export type APIBrandSettings = {
  bannerSettings?: APIBannerSettings | null;
  logoSettings?: APILogoSettings | null;
};

export const AI_SUGGESTED_PROMPT_TYPES = [
  'explain',
  'highlight',
  'write',
] as const;

export type APIAiSuggestedPromptType =
  (typeof AI_SUGGESTED_PROMPT_TYPES)[number];

export const DEFAULT_AI_SUGGESTED_PROMPT_TYPE: APIAiSuggestedPromptType =
  'explain';

export const toAiSuggestedPromptType = (
  type: unknown
): APIAiSuggestedPromptType =>
  AI_SUGGESTED_PROMPT_TYPES.includes(type as APIAiSuggestedPromptType)
    ? (type as APIAiSuggestedPromptType)
    : DEFAULT_AI_SUGGESTED_PROMPT_TYPE;

export const AI_SUGGESTED_PROMPT_SLOTS = 3;

export const MAX_AI_SUGGESTED_PROMPT_LENGTH = 120;

export type APIAiSuggestedPrompt = {
  type: APIAiSuggestedPromptType;
  text: string;
};

// one slot per built-in home/Discover prompt, in the order they are shown.
// `null` leaves that slot's built-in prompt in place.
// the API does not cap the count, so there may be more slots than built-in
// prompts; those are shown after them rather than dropped.
export type APIAiSuggestedPrompts = (APIAiSuggestedPrompt | null)[];

const toAiSuggestedPrompt = (prompt: unknown): APIAiSuggestedPrompt | null => {
  if (typeof prompt !== 'object' || prompt === null) return null;

  const { type, text } = prompt as Partial<APIAiSuggestedPrompt>;
  if (typeof text !== 'string' || text.trim().length === 0) return null;

  return { type: toAiSuggestedPromptType(type), text: text.trim() };
};

const lastOverriddenIndex = (prompts: APIAiSuggestedPrompts): number =>
  prompts.reduce((last, prompt, index) => (prompt ? index : last), -1);

export const toAiSuggestedPrompts = (
  prompts: unknown
): APIAiSuggestedPrompts => {
  if (!Array.isArray(prompts)) return [];

  const slots = prompts.map(toAiSuggestedPrompt);
  return slots.slice(0, lastOverriddenIndex(slots) + 1);
};

export type APIOrganizationSettings = {
  defaultWsPermissions: PermissionAccessLevel | null;
  aiSuggestedPrompts?: APIAiSuggestedPrompts;
  logo?: string;
  emergencyDataSecurityContacts?: string[];
  ipAllowlistDeniedAccessMessage?: string;
  constraintEnforcementMode?: APIConstraintEnforcementMode;
  brandSettings?: APIBrandSettings | null;
  language?: string;
  /**
   * Component type aliases that are considered "applications" for billing purposes.
   */
  applicationAliases?: {
    aliases: string[];
  };
  confidentialFieldsLimit?: number;
  defaultCurrency?: string | null;
};
